import { useCallback, useMemo, MouseEvent } from 'react';
import styled from '@emotion/styled';
import { withTheme } from '@mui/styles';
import { useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FormikProps } from 'formik';

import {
  StyledContainer,
  StyledSettingsContainer,
  StyledSubTitle,
  StyledText,
  StyledCloudRecordingsLogo,
  StyledButton,
} from '@selfserve/react-ui';
import { billingCycles, buttonIds } from '@selfserve/react-utils';
import { PlanInfo } from '@selfserve/react-common-data';

import { PackageDetail, Plans } from '../data';
import { FormValues } from './build-your-plan';
import { AddonCounter, AddonDropdown, AddonRadio } from '../ui';
import { planControllers, planControllerTypes } from '../constants';

const StyledAddOnInfoContainer = styled.div(({ theme }: { theme?: any }) => ({
  marginRight: 'auto',
}));

const StyledAddOnName = styled(StyledText)`
  line-height: 26px;
`;

const StyledAddButton = styled(StyledButton)(({ theme }: { theme?: any }) => ({
  minWidth: 'fit-content',
  maxWidth: 'fit-content',
  maxHeight: '40px',
  [theme.breakpoints.down('sm')]: {
    marginTop: theme.spacing(2),
  },
}));

interface Props extends PackageDetail {
  theme: any;
  formik: FormikProps<FormValues>;
}

export const AddOn = withTheme(
  ({
    theme,
    formik: {
      values: { billingCycle, addons },
      setFieldValue,
    },
    yearly_addons,
    monthly_addons,
    plans,
    leading_plan,
  }: Props) => {
    const { t } = useTranslation();
    const matchesSm = useMediaQuery(theme.breakpoints.down('sm'));
    const addonsGroup = useMemo(() => {
      switch (billingCycle) {
        default:
        case billingCycles.ANNUALLY:
          return yearly_addons?.addon_group;
        case billingCycles.MONTHLY:
          return monthly_addons?.addon_group;
      }
    }, [yearly_addons, monthly_addons, billingCycle]);

    const handleClick = useCallback(
      ({
          group_info,
          group_items,
        }: {
          group_info: PlanInfo;
          group_items: PlanInfo[];
        }) =>
        (event: MouseEvent<HTMLButtonElement>) => {
          event.preventDefault();
          const [item] = group_items;
          const { name, controller } = item || {};
          const controllerValues = group_info?.controllerValues?.length
            ? group_info?.controllerValues
            : [];
          const addedItem = group_items.find(
            ({ name }: PlanInfo) => addons[name || '']
          );
          const addOnName = name || '';
          const updatedAddons = { ...addons };
          if (updatedAddons[addedItem?.name as string]) {
            delete updatedAddons[addedItem?.name as string];
          } else {
            updatedAddons[addOnName] = {
              quantity:
                controller === planControllers.DROPDOWN
                  ? controllerValues[0].value || 1
                  : 1,
              name: addOnName,
            };
          }
          setFieldValue('addons', updatedAddons);
        },
      [addons, setFieldValue]
    );

    const setValue = useCallback((name: string, value: string | number) => {
      setFieldValue('addons', {
        ...addons,
        [name as string]: {
          quantity: +value,
          name,
        },
      });
    }, []);

    const handleDropdownChange = useCallback(
      (name: string) => (value: string) => {
        setValue(name, value);
      },
      []
    );

    const handleRadioChange = useCallback(
      (name: string, arr: PlanInfo[]) => () => {
        const updatedAddons = { ...addons };
        arr.forEach(({ name }: PlanInfo) => delete updatedAddons[name || '']);
        updatedAddons[name] = { name, quantity: 1 };
        setFieldValue('addons', updatedAddons);
      },
      [addons]
    );

    if (!addonsGroup?.length) return null;

    return (
      <StyledContainer fitHeight>
        <StyledSubTitle className="sub-header" as="span">
          {t('common.add-on')}
        </StyledSubTitle>
        {addonsGroup?.map(({ group_info, group_items }) => {
          const { name, headline, controller } = group_info || {};
          const addOnName = group_items?.find(
            ({ name }) => addons[name || '']
          )?.name;
          const isAdded = !!addOnName;
          const selectOptions =
            group_info?.controllerValues?.map(({ key: text, value }) => ({
              text: text as string,
              value: String(value),
            })) || [];
          return (
            <StyledSettingsContainer
              key={group_info?.name}
              flexWrap={matchesSm}
              row={!matchesSm}
            >
              {!matchesSm && <StyledCloudRecordingsLogo />}
              <StyledAddOnInfoContainer>
                <StyledAddOnName className="addon-name" bold>
                  {name}
                </StyledAddOnName>
                <StyledText as="div" className="plan-info">
                  {headline}
                </StyledText>
                <div className="addon-options ng-star-inserted">
                  {isAdded &&
                    ((key: planControllerTypes | undefined) => {
                      switch (key) {
                        case planControllers.RADIO:
                          return (
                            <AddonRadio
                              group_items={group_items}
                              addons={addons}
                              addOnName={addOnName}
                              plans={plans as Plans}
                              billingCycle={billingCycle}
                              handleRadioChange={handleRadioChange}
                            />
                          );
                        case planControllers.COUNTER:
                          return (
                            <AddonCounter
                              planName={leading_plan?.name as string}
                              group_items={group_items}
                              addons={addons}
                              addOnName={addOnName}
                              setValue={setValue}
                            />
                          );
                        case planControllers.DROPDOWN:
                          return (
                            <AddonDropdown
                              group_items={group_items}
                              addons={addons}
                              addOnName={addOnName}
                              selectOptions={selectOptions}
                              handleDropdownChange={handleDropdownChange}
                            />
                          );
                        default:
                          return null;
                      }
                    })(controller)}
                </div>
              </StyledAddOnInfoContainer>
              <StyledAddButton
                id={buttonIds.addOns}
                variant="borderless"
                size="large"
                onClick={handleClick({
                  group_info: group_info as PlanInfo,
                  group_items: group_items as PlanInfo[],
                })}
                added={isAdded}
                withoutMargin
                isBorderless
                isAddOn
              >
                <span className="p-button-label">
                  {t(`common.${isAdded ? 'added-to-plan' : 'add-to-plan'}`)}
                </span>
              </StyledAddButton>
            </StyledSettingsContainer>
          );
        })}
      </StyledContainer>
    );
  }
);
